import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { ChevronDown, ChevronUp } from "lucide-react"
import { SessionDisplay } from '@/components/SessionDisplay'
import { AmendmentSettingsPanel } from './AmendmentSettingsPanel'
import type { APISession } from '@/types/session'
import type { DataSource } from '@/hooks/admin/useDatasourceState'
import type { AmendmentDetectionSettings } from '@/api/admin-settings-api'

interface SessionSyncControlsProps {
  session: APISession
  selectedDataSource: DataSource
  sessionEnabled: boolean
  amendmentSettings: AmendmentDetectionSettings
  isExpanded: boolean
  isMutating: boolean
  onSyncToggle: (session: APISession, enabled: boolean) => void
  onToggleExpanded: () => void
  onAmendmentSettingChange: (session: APISession, setting: string, value: boolean | number) => void
  onNumericInputChange: (session: APISession, setting: 'dpi' | 'images_per_page', value: number) => void
}

export function SessionSyncControls({
  session,
  selectedDataSource,
  sessionEnabled,
  amendmentSettings,
  isExpanded,
  isMutating,
  onSyncToggle,
  onToggleExpanded,
  onAmendmentSettingChange,
  onNumericInputChange
}: SessionSyncControlsProps) {
  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Main session row with sync toggle and key amendment controls */}
      <div className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
        <div className="flex items-center gap-3 flex-1">
          <div className={`w-2 h-2 rounded-full ${sessionEnabled ? 'bg-green-500' : 'bg-slate-300'}`}></div>
          <SessionDisplay session={session} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor={`sync-${session}`} className="text-xs text-slate-600 dark:text-slate-400">
              Sync
            </Label>
            <Switch
              id={`sync-${session}`}
              checked={sessionEnabled}
              onCheckedChange={(enabled) => onSyncToggle(session, enabled)}
              disabled={isMutating}
            />
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0"
            onClick={onToggleExpanded}
          >
            {isExpanded ? 
              <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Detailed amendment settings - expandable */}
      {isExpanded && sessionEnabled && (
        <AmendmentSettingsPanel
          session={session}
          selectedDataSource={selectedDataSource}
          amendmentSettings={amendmentSettings}
          isMutating={isMutating}
          onAmendmentSettingChange={onAmendmentSettingChange}
          onNumericInputChange={onNumericInputChange}
        />
      )}

      {/* Warning for disabled sync */}
      {!sessionEnabled && (
        <div className="border-t bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Enable {session} sync to configure amendment detection
          </p>
        </div>
      )}
    </div>
  )
}
